import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { finalize, first, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ToastService } from '@storybook/services/toast/toast.service';
import { TrackingPageService } from '@pages/checkout-app/checkout-layout/main/components/tracking-page/tracking-page.service';
import { TrackingPageDefaultValues } from '@pages/checkout-app/checkout-layout/main/components/tracking-page/tracking-page.models';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shop } from '@services/shops/shop.model';
import { ShopService } from '@services/shops/shop.service';
import { ITrackingPage } from '@pdqdev/pdq-shared/dist/interfaces/shopPlugin/ITrackingPage';

export interface IntegrationFormGroup {
  enabled: FormControl<boolean>;
  sdkKey?: FormControl<string>;
  customerKey: FormControl<string>;
  appearance?: FormControl<string>;
}

export interface IntegrationsTypeFormGroup {
  [ITrackingPage.IntegrationType.UPTICK]: FormGroup<IntegrationFormGroup>;
  [ITrackingPage.IntegrationType.DISCO]: FormGroup<IntegrationFormGroup>;
  [ITrackingPage.IntegrationType.FALCON]: FormGroup<IntegrationFormGroup>;
}

@Component({
  selector: 'tracking-page-main',
  templateUrl: './tracking-page-main.component.html',
  styleUrls: ['./tracking-page-main.component.scss', '../../tracking-page.component.scss'],
  standalone: false,
})
export class TrackingPageMainComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() trackingPageConfiguration: ShopPlugin.TrackingPage.Configuration;
  @Input() isLoading = true;
  @Input() shop: Shop;
  @Input() shopRequirements: { updateRequired: boolean; updateAppUrl: string };
  trackingPageMetrics$ = this.trackingPageService.trackingPageMetrics$;
  isTrackingPageLoading$ = this.trackingPageService.isTrackingPageMetricsLoading$;
  isSaving: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private trackingPageService: TrackingPageService,
    private toastService: ToastService,
    private shopService: ShopService,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trackingPageConfiguration) {
      this.initFormGroup(this.trackingPageConfiguration);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initFormGroup(loadedValues?: ShopPlugin.TrackingPage.Configuration) {
    this.formGroup = new UntypedFormGroup({
      settings: new UntypedFormGroup({
        internalName: new UntypedFormControl(loadedValues?.settings?.internalName ?? null),
        accentColor: new UntypedFormControl(
          loadedValues?.settings?.accentColor ?? TrackingPageDefaultValues.settings.accentColor,
        ),
        colorScheme: new UntypedFormControl(
          loadedValues?.settings?.colorScheme ?? TrackingPageDefaultValues.settings.colorScheme,
        ),
        url: new UntypedFormControl(loadedValues?.settings?.url ?? null, [Validators.required]),
        stalledShipmentHours: new UntypedFormControl(loadedValues?.settings?.stalledShipmentHours ?? null),
        showProductsForShipment: new UntypedFormControl(loadedValues?.settings?.showProductsForShipment ?? false),
        showPricesForEachShipmentLineItem: new UntypedFormControl(
          loadedValues?.settings?.showPricesForEachShipmentLineItem ?? false,
        ),
        showDestinationMap: new UntypedFormControl(loadedValues?.settings?.showDestinationMap ?? false),
        searchPage: new UntypedFormGroup({
          titleAlignment: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.titleAlignment ??
              TrackingPageDefaultValues.settings.searchPage.titleAlignment,
          ),
          title: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.title ?? TrackingPageDefaultValues.settings.searchPage.title,
          ),
          inputPlaceholder: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.inputPlaceholder ??
              TrackingPageDefaultValues.settings.searchPage.inputPlaceholder,
          ),
          verifyByTrackingOrOrderNumber: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.verifyByTrackingOrOrderNumber ??
              TrackingPageDefaultValues.settings.searchPage.verifyByTrackingOrOrderNumber,
          ),
          verificationStrategy: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.verificationStrategy ??
              TrackingPageDefaultValues.settings.searchPage.verificationStrategy,
          ),
          emailPlaceholder: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.emailPlaceholder ??
              TrackingPageDefaultValues.settings.searchPage.emailPlaceholder,
          ),
          phonePlaceholder: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.phonePlaceholder ??
              TrackingPageDefaultValues.settings.searchPage.phonePlaceholder,
          ),
          buttonText: new UntypedFormControl(
            loadedValues?.settings?.searchPage?.buttonText ?? TrackingPageDefaultValues.settings.searchPage.buttonText,
            [Validators.required],
          ),
        }),
      }),
      widgets: new UntypedFormGroup({
        couponCodes: new UntypedFormArray([]),
        productSection: new UntypedFormGroup({
          title: new UntypedFormControl(
            loadedValues?.widgets?.productSection?.title ?? TrackingPageDefaultValues.widgets.productSection.title,
            [Validators.required],
          ),
          bodyText: new UntypedFormControl(
            loadedValues?.widgets?.productSection?.bodyText ??
              TrackingPageDefaultValues.widgets.productSection.bodyText,
          ),
          showProductSection: new UntypedFormControl(
            loadedValues?.widgets?.productSection?.showProductSection ?? false,
          ),
        }),
        details: new UntypedFormGroup({
          showTrackingDetails: new UntypedFormControl(
            loadedValues?.widgets?.details?.showTrackingDetails ??
              TrackingPageDefaultValues.widgets.details.showTrackingDetails,
          ),

          displayMessageForUntrackedShipments: new UntypedFormControl(
            loadedValues?.widgets?.details?.displayMessageForUntrackedShipments ??
              TrackingPageDefaultValues.widgets.details.displayMessageForUntrackedShipments,
          ),
          carriersToDisplayMessageFor: new UntypedFormControl(
            loadedValues?.widgets?.details?.carriersToDisplayMessageFor ??
              TrackingPageDefaultValues.widgets.details.carriersToDisplayMessageFor,
          ),
          messageForUntrackedShipments: new UntypedFormControl(
            loadedValues?.widgets?.details?.messageForUntrackedShipments ??
              TrackingPageDefaultValues.widgets.details.messageForUntrackedShipments,
          ),
          displayMessageWhenHandedOverToDifferentCarrier: new UntypedFormControl(
            loadedValues?.widgets?.details?.displayMessageWhenHandedOverToDifferentCarrier ??
              TrackingPageDefaultValues.widgets.details.displayMessageWhenHandedOverToDifferentCarrier,
          ),
          messageWhenHandedOverToDifferentCarrier: new UntypedFormControl(
            loadedValues?.widgets?.details?.messageWhenHandedOverToDifferentCarrier ??
              TrackingPageDefaultValues.widgets.details.messageWhenHandedOverToDifferentCarrier,
          ),
          showDeliveryStatus: new UntypedFormControl(
            loadedValues?.widgets?.details?.showDeliveryStatus ??
              TrackingPageDefaultValues.widgets.details.showDeliveryStatus,
          ),
          showDetailedTrackingHistory: new UntypedFormControl(
            loadedValues?.widgets?.details?.showDetailedTrackingHistory ??
              TrackingPageDefaultValues.widgets.details.showDetailedTrackingHistory,
          ),
          displayTrackingNumber: new UntypedFormControl(
            loadedValues?.widgets?.details?.displayTrackingNumber ??
              TrackingPageDefaultValues.widgets.details.displayTrackingNumber,
          ),
        }),
        simpleTracker: new UntypedFormGroup({
          showTrackingProgress: new UntypedFormControl(
            loadedValues?.widgets?.simpleTracker?.showTrackingProgress ?? false,
          ),
          unsuccessfulTrackingStatusColor: new UntypedFormControl(
            loadedValues?.widgets?.simpleTracker.unsuccessfulTrackingStatusColor ??
              TrackingPageDefaultValues.widgets.simpleTracker.unsuccessfulTrackingStatusColor,
          ),
          showFutureSteps: new UntypedFormControl(
            loadedValues?.widgets?.simpleTracker.showFutureSteps ??
              TrackingPageDefaultValues.widgets.simpleTracker.showFutureSteps,
          ),
        }),
        deliveryPromise: new FormGroup({
          showShippingMethodPromise: new FormControl<boolean>(
            this.getDeliveryPromiseValue(loadedValues, 'showShippingMethodPromise'),
          ),
          showExpectedLateIndicator: new FormControl<boolean>(
            this.getDeliveryPromiseValue(loadedValues, 'showExpectedLateIndicator'),
          ),
          showCarrierETA: new FormControl<boolean>(this.getDeliveryPromiseValue(loadedValues, 'showCarrierETA')),
        }),
        integrations: new FormGroup<IntegrationsTypeFormGroup>({
          uptick: new FormGroup<IntegrationFormGroup>({
            enabled: new FormControl(loadedValues?.widgets?.integrations?.uptick?.enabled ?? false),
            customerKey: new FormControl(loadedValues?.widgets?.integrations?.uptick?.customerKey ?? ''),
          }),
          disco: new FormGroup<IntegrationFormGroup>({
            enabled: new FormControl(loadedValues?.widgets?.integrations?.disco?.enabled ?? false),
            customerKey: new FormControl(loadedValues?.widgets?.integrations?.disco?.customerKey ?? ''),
          }),
          falcon: new FormGroup<IntegrationFormGroup>(
            {
              enabled: new FormControl(loadedValues?.widgets?.integrations?.falcon?.enabled ?? false),
              sdkKey: new FormControl(loadedValues?.widgets?.integrations?.falcon?.sdkKey ?? ''),
              customerKey: new FormControl(loadedValues?.widgets?.integrations?.falcon?.customerKey ?? ''),
              appearance: new FormControl(loadedValues?.widgets?.integrations?.falcon?.appearance || 'modal'),
            },
            { validators: this.falconConditionalValidator },
          ),
        }),
      }),
    });

    const searchPage = this.formGroup.get('settings.searchPage') as UntypedFormGroup;

    this.applyVerificationStrategyValidators(searchPage);

    searchPage
      .get('verificationStrategy')!
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyVerificationStrategyValidators(searchPage));
  }

  private noBlankStringValidator(control: AbstractControl): ValidationErrors | null {
    const v = control.value;
    return v !== null && typeof v === 'string' && v.trim() === '' ? { blank: true } : null;
  }

  private applyVerificationStrategyValidators(searchPage: UntypedFormGroup) {
    const strategy = searchPage.get('verificationStrategy')?.value as string;
    const email = searchPage.get('emailPlaceholder') as UntypedFormControl;
    const phone = searchPage.get('phonePlaceholder') as UntypedFormControl;

    const currentEmailValue = email.value;
    const currentPhoneValue = phone.value;

    email.clearValidators();
    phone.clearValidators();

    switch (strategy) {
      case ITrackingPage.VerificationStrategy.EMAIL_ONLY:
        email.setValidators([Validators.required, this.noBlankStringValidator]);
        if (!currentEmailValue || currentEmailValue.trim() === '') {
          email.setValue(TrackingPageDefaultValues.settings.searchPage.emailPlaceholder);
        }
        break;

      case ITrackingPage.VerificationStrategy.PHONE_ONLY:
        phone.setValidators([Validators.required, this.noBlankStringValidator]);
        if (!currentPhoneValue || currentPhoneValue.trim() === '') {
          phone.setValue(TrackingPageDefaultValues.settings.searchPage.phonePlaceholder);
        }
        break;

      case ITrackingPage.VerificationStrategy.EMAIL_AND_PHONE:
      case ITrackingPage.VerificationStrategy.EMAIL_OR_PHONE:
        email.setValidators([Validators.required, this.noBlankStringValidator]);
        phone.setValidators([Validators.required, this.noBlankStringValidator]);
        if (!currentEmailValue || currentEmailValue.trim() === '') {
          email.setValue(TrackingPageDefaultValues.settings.searchPage.emailPlaceholder);
        }
        if (!currentPhoneValue || currentPhoneValue.trim() === '') {
          phone.setValue(TrackingPageDefaultValues.settings.searchPage.phonePlaceholder);
        }
        break;

      case ITrackingPage.VerificationStrategy.NONE:
      default:
        break;
    }

    email.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    phone.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  falconConditionalValidator(group: FormGroup): null {
    const enabled = group.get('enabled')?.value;
    const sdkKey = group.get('sdkKey') as FormControl;
    const customerKey = group.get('customerKey') as FormControl;

    sdkKey.clearValidators();
    customerKey.clearValidators();

    if (enabled) {
      sdkKey.setValidators([Validators.required]);
      customerKey.setValidators([Validators.required]);
    }

    sdkKey.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    customerKey.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    return null;
  }

  setTrackingPageId(config, id: number) {
    if (id) {
      config.id = id;
    }
  }

  private getDeliveryPromiseValue(
    loadedValues: ShopPlugin.TrackingPage.Configuration | undefined,
    field: 'showShippingMethodPromise' | 'showExpectedLateIndicator' | 'showCarrierETA',
  ): boolean {
    return loadedValues?.widgets?.deliveryPromise?.[field] ?? TrackingPageDefaultValues.widgets.deliveryPromise[field];
  }

  onSave() {
    const conf = { ...this.formGroup.value };
    this.setTrackingPageId(conf, this.trackingPageConfiguration?.id);
    this.isSaving = true;
    this.trackingPageService
      .saveTrackingPageConfiguration(conf, this.shop)
      .pipe(
        first(),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe(
        (res) => {
          this.setTrackingPageId(conf, res?.id);
          this.trackingPageService.trackingPageConfiguration = conf;
          this.toastService.showToast({ mode: 'success', message: 'Tracking page saved successfully' });
        },
        (e) => {
          console.error(e);
          this.toastService.showToast({
            mode: 'danger',
            message: e?.error?.message,
          });
        },
      );
  }

  onUpdateAppPermissions() {
    window.open(this.shopRequirements?.updateAppUrl, '_blank');
  }
}
